import React, { useEffect, useState } from "react";
import "./AddCourseForm.css";
import TextFieldInput from "../../../Atoms/Inputs/TextField/TextFieldInput";
import PrimaryButton from "../../../Atoms/Buttons/Primary/PrimaryButton";
import Button from "@mui/material/Button";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import { Typography } from "@mui/material";
import TextArea from "../../../Atoms/Inputs/TextArea/TextArea";
import useEdit from "../../../../Hooks/useEdit";
import useSave from "../../../../Hooks/useSave";
import { BaseURL, courseURL } from "../../../../Hooks/URL";
import AddIcon from "@mui/icons-material/Add";
import { Formik } from "formik";
import * as Yup from "yup";
import { images } from "../../../../Assets/assetsHelper";
import CloseIcon from "@mui/icons-material/Close";
import { useNavigate } from "react-router-dom";
import AssignmentTurnedInIcon from "@mui/icons-material/AssignmentTurnedIn";
import DeleteIcon from "@mui/icons-material/Delete";
import DragIndicatorIcon from "@mui/icons-material/DragIndicator";
import { PulseLoader } from "react-spinners";
import useUpdate from "../../../../Hooks/useUpdate";
import usePut from "../../../../Hooks/usePut";
import FileAttachmentInput from "../../../Molecules/FileAttachmentInput/FileAttachmentInput";

const CourseSchema = Yup.object().shape({
  title: Yup.string().required("Program name required !!"),
  description: Yup.string().required("Description required !!"),
});

function AddCourseForm({ Edit = false, attributesEdit = false, course, url }) {
  const navigate = useNavigate();
  const [newObject, setNewObject] = useState({});
  const [attributes, setAttribute] = useState(
    course?.attributes?.map((att) => att.attribute) ?? []
  );
  const [existingAttachments, setExistingAttachments] = useState(
    course?.attachments?.map((file, index) => ({
      id: file?.id,
      file: file?.filePath,
      name: file?.name,
      size: 0,
      type: "application/octet-stream",
      isExisting: true,
    })) ?? []
  );
  const [newAttachments, setNewAttachments] = useState([]);
  const [deletedAttachmentIds, setDeletedAttachmentIds] = useState([]);
  const [goEdit, setGoEdit] = useState(false);
  const [goSave, setGoSave] = useState(false);
  const [goUpdate, setGoUpdate] = useState(false);
  const [draggedIndex, setDraggedIndex] = useState(null);
  // const [newAttribute, setNewAttribute] = useState("");
  const { state, loading: editLoading } = useEdit(
    url,
    goEdit,
    setGoEdit,
    newObject,
    true, // Enable file upload for edit
    true // Enable file upload handling
  );
  const { state: updateState, loading: updateLoading } = usePut({
    url: `${courseURL}/${course?.id}/attributes`,
    goEdit: goUpdate,
    setGoEdit: setGoUpdate,
    newObject,
  });
  const { saveState, loading: addLoading } = useSave({
    url: courseURL,
    goSave,
    setGoSave,
    newObject,
    isImage: true,
  });

  // Drag and drop handlers for attributes
  const handleDragStart = (e, index) => {
    setDraggedIndex(index);
    e.dataTransfer.effectAllowed = "move";
    e.dataTransfer.setData("text/html", e.target.outerHTML);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = "move";
  };

  const handleDrop = (e, dropIndex) => {
    e.preventDefault();

    if (draggedIndex === null || draggedIndex === dropIndex) {
      setDraggedIndex(null);
      return;
    }

    const newAttributes = [...attributes];
    const draggedItem = newAttributes[draggedIndex];

    // Remove the dragged item
    newAttributes.splice(draggedIndex, 1);

    // Insert at the new position
    newAttributes.splice(dropIndex, 0, draggedItem);

    setAttribute(newAttributes);
    setDraggedIndex(null);
  };

  const handleDragEnter = (e) => {
    e.preventDefault();
    e.target.style.backgroundColor = "#e3f2fd";
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    if (
      draggedIndex === null ||
      parseInt(e.target.dataset.index) !== draggedIndex
    ) {
      e.target.style.backgroundColor = "#f5f5f5";
    }
  };

  const handleDragEnd = () => {
    setDraggedIndex(null);
  };

  // const handleUploadClick = (e) => {
  //   setNewObject({
  //     ...newObject,
  //     logo: URL.createObjectURL(e.target.files[0]),
  //   });
  //   setFile(URL.createObjectURL(e.target.files[0]));
  // };

  // Initialize attachments when course data changes (for edit mode)
  useEffect(() => {
    if (course && Edit) {
      setExistingAttachments(
        course?.attachments?.map((file, index) => ({
          id: file?.id,
          file: file?.filePath,
          name: file?.name,
          size: 0,
          type: "application/octet-stream",
          isExisting: true,
        })) ?? []
      );
      setNewAttachments([]);
      setDeletedAttachmentIds([]);
    }
  }, [course, Edit]);

  useEffect(() => {
    if (state.dataRes.status == "success") {
      navigate(`/programs/${course?.id}`);
    }
  }, [state]);

  useEffect(() => {
    if (saveState.dataRes.status == "success") {
      navigate("/programs");
    }
  }, [saveState]);

  useEffect(() => {
    if (updateState.dataRes.status == "success") {
      navigate(`/programs/${course?.id}`);
    }
  }, [updateState]);

  return (
    <div className="CourseSmallFormWrapper">
      <Formik
        initialValues={{
          title: Edit ? course?.title ?? "" : "",
          description: Edit ? course?.description ?? "" : "",
        }}
        validationSchema={CourseSchema}
        onSubmit={(values) => {
          // Format attributes for API
          const formattedAttributes = attributes.map((attr, index) => ({
            attribute: attr,
            number: index + 1,
          }));

          if (attributesEdit) {
            // For attributes edit mode, send only attributes
            const apiBody = {
              attributes: formattedAttributes,
            };

            setNewObject(apiBody);
            setGoUpdate(true);
          } else if (Edit) {
            // For edit mode, use the new API structure
            const newAttachmentsFiles = newAttachments.map((att) => att.file);
            const newAttachmentsNames = newAttachments.map((att) => att.name);

            const apiBody = {
              title: values.title,
              description: values.description,
              attachmentsFiles: newAttachmentsFiles,
              deletedAttachmentsIds: deletedAttachmentIds,
              attachmentsNames: newAttachmentsNames,
            };

            setNewObject(apiBody);
            setGoEdit(true);
          } else {
            // For create mode, use the original structure
            const allAttachments = [...existingAttachments, ...newAttachments];
            const attachmentsFiles = allAttachments.map((att) => att.file);
            const attachmentsNames = allAttachments.map((att) => att.name);

            setNewObject({
              ...values,
              attributes: formattedAttributes,
              attachmentsFiles: attachmentsFiles,
              attachmentsNames: attachmentsNames,
            });
            setGoSave(true);
          }
        }}
      >
        {({
          values,
          errors,
          touched,
          handleChange,
          handleBlur,
          handleSubmit,
        }) => (
          <form autoComplete="off" onSubmit={handleSubmit}>
            <div className="CourseFormContainer">
              <div className="LeftForm">
                {/*  <div
                  style={{
                    display: "flex",
                    alignItems: "flex-end",
                    justifyContent: "space-between",
                  }}
                >
                  <div>
                     <Typography
                      style={{ fontWeight: "500", marginBottom: "10px" }}
                    >
                      Program Logo:
                    </Typography>
                    <Button
                      component="label"
                      variant="contained"
                      style={{ marginBottom: "20px" }}
                      startIcon={<CloudUploadIcon />}
                      className="ImageInputButton"
                    >
                      Upload file
                      <input
                        accept="image/*"
                        className={"ImageInput"}
                        id="contained-button-file"
                        type="file"
                        onChange={handleUploadClick}
                      />
                    </Button>
                  </div>
                  <img
                    src={file}
                    style={{
                      width: "120px",
                      maxHeight: "120px",
                    }}
                  />
                </div>*/}

                {!attributesEdit && (
                  <>
                    <label
                      className="aboutLabel"
                      style={{ marginBottom: "3px" }}
                    >
                      Program Name:
                    </label>
                    <TextFieldInput
                      fullWidth
                      type="text"
                      placeholder={"Enter Program Name"}
                      className="Input"
                      // label={"Program Name"}
                      name={"title"}
                      change={handleChange}
                      blur={handleBlur}
                      newValue={values.title}
                    />

                    {errors.title && touched.title && errors.title && (
                      <p className="error">
                        {errors.title && touched.title && errors.title}
                      </p>
                    )}

                    <label className="aboutLabel">About The Program:</label>
                    <TextArea
                      style={{ marginBottom: "20px" }}
                      classes="textAreaInput"
                      label={"Description"}
                      name={"description"}
                      change={handleChange}
                      blur={handleBlur}
                      newValue={values.description}
                    />
                    {errors.description &&
                      touched.description &&
                      errors.description && (
                        <p className="error">
                          {errors.description &&
                            touched.description &&
                            errors.description}
                        </p>
                      )}
                  </>
                )}
                {!attributesEdit && (
                  <FileAttachmentInput
                    existingAttachments={existingAttachments}
                    newAttachments={newAttachments}
                    deletedAttachmentIds={deletedAttachmentIds}
                    onAttachmentUpload={(files) => {
                      const newFiles = files.map((file) => ({
                        file: file,
                        name: file.name,
                        size: file.size,
                        type: file.type,
                        isExisting: false,
                      }));
                      setNewAttachments((prev) => [...prev, ...newFiles]);
                    }}
                    onRemoveAttachment={(index, isExisting) => {
                      if (isExisting) {
                        setExistingAttachments((prev) =>
                          prev.filter((_, idx) => idx !== index)
                        );
                      } else {
                        setNewAttachments((prev) =>
                          prev.filter((_, idx) => idx !== index)
                        );
                      }
                    }}
                    onUpdateDeletedIds={setDeletedAttachmentIds}
                    onUpdateExistingAttachments={setExistingAttachments}
                    onUpdateNewAttachments={setNewAttachments}
                    maxFiles={5}
                    label="Program Attachments:"
                    accept="*/*"
                    disabled={false}
                    baseURL={BaseURL}
                  />
                )}

                {Edit && !attributesEdit ? (
                  ""
                ) : (
                  <>
                    <label
                      className="aboutLabel"
                      style={{ marginBottom: "3px" }}
                    >
                      Program Attribute Reports:
                    </label>
                    <TextFieldInput
                      Required={false}
                      fullWidth
                      type="text"
                      setAttribute={setAttribute}
                      attribute={attributes || []}
                      EndAdornment={<AddIcon />}
                      placeholder={"Enter Program Attribute Report"}
                      className="Input"
                      // label={"Attribute Report"}
                      name={"report"}
                    />
                    {attributes.length > 0 && (
                      <div style={{ marginTop: "10px" }}>
                        <Typography
                          style={{
                            fontSize: "14px",
                            color: "#666",
                            marginBottom: "8px",
                          }}
                        >
                          Program Standards ({attributes.length}):
                        </Typography>
                        <Typography
                          style={{
                            fontSize: "12px",
                            color: "#888",
                            marginBottom: "12px",
                            fontStyle: "italic",
                          }}
                        >
                          💡 Drag standards by the menu icon to reorder them
                        </Typography>
                        {attributes.map((attribute, index) => (
                          <div
                            key={index}
                            draggable
                            data-index={index}
                            onDragStart={(e) => handleDragStart(e, index)}
                            onDragOver={handleDragOver}
                            onDragEnter={handleDragEnter}
                            onDragLeave={handleDragLeave}
                            onDrop={(e) => handleDrop(e, index)}
                            onDragEnd={handleDragEnd}
                            style={{
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "space-between",
                              padding: "8px 12px",
                              backgroundColor:
                                draggedIndex === index ? "#e3f2fd" : "#f5f5f5",
                              borderRadius: "4px",
                              marginBottom: "8px",
                              border:
                                draggedIndex === index
                                  ? "2px dashed #1976d2"
                                  : "1px solid #ddd",
                              cursor: "move",
                              transition: "all 0.2s ease",
                              opacity: draggedIndex === index ? 0.5 : 1,
                              transform:
                                draggedIndex === index
                                  ? "scale(1.02)"
                                  : "scale(1)",
                            }}
                          >
                            <div
                              style={{
                                display: "flex",
                                alignItems: "center",
                                flex: 1,
                              }}
                            >
                              <DragIndicatorIcon
                                style={{
                                  marginRight: "8px",
                                  color: "#666",
                                  cursor: "grab",
                                }}
                                onMouseDown={(e) => e.preventDefault()}
                              />
                              <div>
                                <Typography
                                  style={{
                                    fontSize: "14px",
                                    fontWeight: "500",
                                  }}
                                >
                                  {attribute}
                                </Typography>
                                <Typography
                                  style={{
                                    fontSize: "12px",
                                    color: "#666",
                                  }}
                                >
                                  Order: {index + 1}
                                </Typography>
                              </div>
                            </div>
                            <div
                              style={{
                                display: "flex",
                                alignItems: "center",
                              }}
                            >
                              <Button
                                onClick={() => {
                                  const updatedAttribute = attributes.filter(
                                    (_, idx) => idx !== index
                                  );
                                  setAttribute(updatedAttribute);
                                }}
                                style={{
                                  minWidth: "auto",
                                  padding: "4px",
                                  color: "#d32f2f",
                                }}
                              >
                                <DeleteIcon fontSize="small" />
                              </Button>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}

                    <div
                      style={{
                        display: "flex",
                        gap: "10px",
                        alignItems: "center",
                        width: "100%",
                        flexWrap: "wrap",
                      }}
                    >
                      {/* {attributes.map((a, index) => (
                        <p
                          key={index}
                          style={{
                            padding: "20px 15px 10px",
                            borderRadius: "4px",
                            background: "#012369",
                            color: "white",
                            position: "relative",
                            cursor: "pointer",
                            marginBottom: "5px",
                          }}
                          onClick={() => {
                            const updatedAttribute = attributes.filter(
                              (_, idx) => idx !== index
                            );
                            setAttribute(updatedAttribute);
                          }}
                        >
                          {a}
                          <div
                            style={{
                              cursor: "pointer",
                              position: "absolute",
                              right: "5px",
                              top: "-2px",
                            }}
                            onClick={() => {
                              const updatedAttribute = attributes.filter(
                                (_, idx) => idx !== index
                              );
                              setAttribute(updatedAttribute);
                            }}
                          >
                            x
                          </div>
                        </p>
                      ))} */}
                    </div>
                  </>
                )}
              </div>
            </div>

            <div style={{ display: "flex", justifyContent: "center" }}>
              <button
                className="actionButton primary addButton"
                onClick={handleSubmit}
                disabled={Edit ? editLoading : addLoading}
              >
                {Edit ? (
                  attributesEdit ? (
                    updateLoading ? (
                      <PulseLoader color="white" size={8} />
                    ) : (
                      "Edit"
                    )
                  ) : editLoading ? (
                    <PulseLoader color="white" size={8} />
                  ) : (
                    "Edit"
                  )
                ) : addLoading ? (
                  <PulseLoader color="white" size={8} />
                ) : (
                  "Add"
                )}
              </button>
            </div>
          </form>
        )}
      </Formik>
    </div>
  );
}

export default AddCourseForm;
